// src/pages/DriverTrip.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Import Lucide Icons
import {
  Truck,
  MapPin,
  Navigation,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  Users,
  Flag,
  Circle,
  Map as MapIcon,
  Gauge,
  Navigation2,
  Clock,
  Phone,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

// Import vehicle icons
import carIcon from "../assets/car.png";
import vanIcon from "../assets/van.png";
import busIcon from "../assets/bus.png";
import lorryIcon from "../assets/lorry.png";
import suvIcon from "../assets/suv.png";
import cabIcon from "../assets/cab.png";
import forkliftIcon from "../assets/fork lift.png";
import dockyardIcon from "../assets/dockyard.png";
import cherryIcon from "../assets/cherry.png";
import craneIcon from "../assets/crane.jpg";
import DriverLayout from "../components/DriverLayout";
import CallEmployeeButton from "../components/CallEmployeeButton";
import HireAssistanceBubble from "../components/HireAssistanceBubble";
import { useDispatch, useSelector } from "react-redux";
import api from "../services/api";
import { calculateDistance, estimateETA, calculateFare } from "../services/mapsService";
import locationWS from "../services/locationWebSocket";
import trackingWS from "../services/trackingWebSocket";
import bookingWS from "../services/bookingWebSocket";
import chatWS from "../services/chatWebSocket";
import EmergencyAlertModal from "../components/EmergencyAlertModal";
import { updateEmergency } from "../services/vehicleService";
import { fetchBookings } from "../actions/BookingActions";
import { getForkBookingDetails, postForkBookingForDriver } from "../services/bookingService";
import { fetchUsers } from "../actions/UserActions";
import { fetchVehicles } from "../actions/VehicleActions";

// Fix Leaflet icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Helper to validate and parse coordinates
const parseCoordinate = (value) => {
  const parsed = parseFloat(value);
  return !isNaN(parsed) ? parsed : null;
};

const ensureValidLatLng = (lat, lng) => {
  const validLat = parseCoordinate(lat);
  const validLng = parseCoordinate(lng);
  if (validLat !== null && validLng !== null) {
    return [validLat, validLng];
  }
  return null;
};

// Get vehicle icon based on type
const getVehicleIcon = (vehicleType) => {
  const type = vehicleType?.toUpperCase()?.trim() || "C";
  let iconUrl = carIcon;
  
  if (type === "V" || type.includes("VAN")) iconUrl = vanIcon;
  else if (type === "B" || type.includes("BUS")) iconUrl = busIcon;
  else if (type === "L" || type.includes("LORRY") || type.includes("TRUCK")) iconUrl = lorryIcon;
  else if (type === "S" || type.includes("SUV")) iconUrl = suvIcon;
  else if (type === "CA" || type.includes("CAB") || type.includes("TAXI")) iconUrl = cabIcon;
  else if (type === "F" || type.includes("FORKLIFT") || type.includes("FORK")) iconUrl = forkliftIcon;
  else if (type.includes("DOCKYARD")) iconUrl = dockyardIcon;
  else if (type === "CH" || type.includes("CHERRY")) iconUrl = cherryIcon;
  else if (type === "CR" || type.includes("CRANE")) iconUrl = craneIcon;
  
  return new L.Icon({
    iconUrl: iconUrl,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });
};

const pickupIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png",
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
});

const destinationIcon = new L.Icon({
  iconUrl: "https://cdn-icons-png.flaticon.com/512/149/149060.png",
  iconSize: [35, 35],
  iconAnchor: [17, 35],
  popupAnchor: [0, -35],
});

// Map auto-fit component
const FitBounds = ({ bounds }) => {
  const map = useMap();
  const hasFitted = useRef(false);

  useEffect(() => {
    if (bounds && bounds.length >= 2 && !hasFitted.current) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
      hasFitted.current = true;
    }
  }, [bounds, map]);
  return null;
};

// Fetch route from OpenRouteService
const fetchRoute = async (start, end) => {
  const apiKey = process.env.REACT_APP_ORS_KEY;
  if (!apiKey) return null;
  try {
    const url = `https://api.openrouteservice.org/v2/directions/driving-car?api_key=${apiKey}&start=${start[1]},${start[0]}&end=${end[1]},${end[0]}`;
    const res = await fetch(url);
    const data = await res.json();
    const coords = data.features?.[0]?.geometry?.coordinates;
    if (coords) {
      return coords.map(([lng, lat]) => [lat, lng]);
    }
  } catch (e) {
    console.error("Route fetch error:", e);
  }
  return null;
};

// Status color mapping
const getStageConfig = (stage) => {
  const configs = {
    accepted: {
      label: "New Booking",
      color: "bg-amber-100 text-amber-700",
      icon: Clock,
      borderColor: "border-amber-200",
      bgLight: "bg-amber-50",
      buttonColor: "emerald",
    },
    heading_to_pickup: {
      label: "Heading to Pickup",
      color: "bg-blue-100 text-blue-700",
      icon: Navigation,
      borderColor: "border-blue-200",
      bgLight: "bg-blue-50",
      buttonColor: "blue",
    },
    trip_started: {
      label: "Trip in Progress",
      color: "bg-purple-100 text-purple-700",
      icon: Gauge,
      borderColor: "border-purple-200",
      bgLight: "bg-purple-50",
      buttonColor: "purple",
    },
    completed: {
      label: "Completed",
      color: "bg-emerald-100 text-emerald-700",
      icon: CheckCircle,
      borderColor: "border-emerald-200",
      bgLight: "bg-emerald-50",
      buttonColor: "emerald",
    },
  };
  return configs[stage] || configs.accepted;
};

// Slider Button Component
const SliderButton = ({ 
  onConfirm, 
  icon: Icon, 
  label, 
  confirmLabel, 
  color = "emerald",
  isLoading = false,
  disabled = false
}) => {
  const [isSliding, setIsSliding] = useState(false);
  const [slideProgress, setSlideProgress] = useState(0);
  const sliderRef = useRef(null);
  const startX = useRef(0);
  const startProgress = useRef(0);
  const isDragging = useRef(false);

  const colorClasses = {
    emerald: {
      bg: "bg-emerald-600",
      hover: "hover:bg-emerald-700",
      light: "bg-emerald-50",
      text: "text-emerald-600",
      border: "border-emerald-200",
      sliderTrack: "bg-emerald-100",
    },
    blue: {
      bg: "bg-blue-600",
      hover: "hover:bg-blue-700",
      light: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-200",
      sliderTrack: "bg-blue-100",
    },
    purple: {
      bg: "bg-purple-600",
      hover: "hover:bg-purple-700",
      light: "bg-purple-50",
      text: "text-purple-600",
      border: "border-purple-200",
      sliderTrack: "bg-purple-100",
    },
  };

  const colors = colorClasses[color];

  const handleTouchStart = (e) => {
    if (disabled || isLoading) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    startX.current = clientX;
    startProgress.current = slideProgress;
    isDragging.current = true;
    setIsSliding(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || disabled || isLoading) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - startX.current;
    const sliderWidth = sliderRef.current?.offsetWidth || 300;
    const newProgress = Math.min(100, Math.max(0, startProgress.current + (deltaX / sliderWidth) * 100));
    setSlideProgress(newProgress);
  };

  const handleTouchEnd = () => {
    if (!isDragging.current || disabled || isLoading) {
      isDragging.current = false;
      setIsSliding(false);
      return;
    }
    isDragging.current = false;
    setIsSliding(false);
    
    if (slideProgress >= 85) {
      onConfirm();
      // Reset after action
      setTimeout(() => setSlideProgress(0), 500);
    } else {
      // Reset slider
      setSlideProgress(0);
    }
  };

  const resetSlider = () => {
    setSlideProgress(0);
    setIsSliding(false);
  };

  useEffect(() => {
    if (disabled) {
      resetSlider();
    }
  }, [disabled]);

  return (
    <div className="relative w-full">
      <div 
        ref={sliderRef}
        className={`relative w-full h-14 rounded-xl overflow-hidden cursor-grab active:cursor-grabbing select-none
          ${colors.sliderTrack} border ${colors.border}`}
        onMouseDown={handleTouchStart}
        onMouseMove={handleTouchMove}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Slider Fill */}
        <div 
          className={`absolute left-0 top-0 h-full ${colors.bg} transition-all duration-50 ease-out`}
          style={{ width: `${slideProgress}%` }}
        />
        
        {/* Slider Thumb */}
        <div 
          className={`absolute top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center transition-all duration-50
            ${isSliding ? 'scale-105' : 'scale-100'}`}
          style={{ left: `calc(${slideProgress}% - 24px)` }}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          ) : (
            <ChevronRight size={20} className="text-gray-600" />
          )}
        </div>
        
        {/* Slider Text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            {slideProgress > 0 ? (
              <>{Math.round(slideProgress)}%</>
            ) : (
              <>
                <Icon size={16} className={colors.text} />
                <span>Slide to {label}</span>
              </>
            )}
          </span>
        </div>
      </div>
      <p className="text-xs text-gray-400 text-center mt-2">
        ← Slide right to confirm →
      </p>
    </div>
  );
};

const DriverTrip = () => {
  const dispatch = useDispatch();
  const getBookings = async () => dispatch(fetchBookings());
  const getUsers = async () => dispatch(fetchUsers());
  const getVehicles = async () => dispatch(fetchVehicles());
  const { authKey, userDetails } = useSelector((state) => state.auth);
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [vehicleType, setVehicleType] = useState("C");
  const [tripStage, setTripStage] = useState("accepted");
  const [driverLocation, setDriverLocation] = useState(null);
  const [routeToPickup, setRouteToPickup] = useState([]);
  const [routeToDestination, setRouteToDestination] = useState([]);
  const [distanceToPickup, setDistanceToPickup] = useState(null);
  const [distanceToDestination, setDistanceToDestination] = useState(null);
  const [employeePhoneNumber, setEmployeePhoneNumber] = useState(null);
  const [activeHire, setActiveHire] = useState(null);
  const [hireStage, setHireStage] = useState('accepted');
  const [isEmergencyModalOpen, setIsEmergencyModalOpen] = useState(false);
  const [isSendingEmergency, setIsSendingEmergency] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const lastRouteRefresh = useRef(0);
  const locationInterval = useRef(null);

  const getLatestBookingById = async (id) => {
    const allBookings = await getBookings();
    const found = allBookings.find((b) => b.bId?.toString() === id?.toString()) || null;
    if (found && (found.bsTime || found.beTime)) return found;

    try {
      const forkDetails = await getForkBookingDetails(id);
      if (Array.isArray(forkDetails) && forkDetails[0]) {
        return { ...found, ...forkDetails[0] };
      }
      if (forkDetails && typeof forkDetails === "object") {
        return { ...found, ...forkDetails };
      }
    } catch (err) {
      console.warn("[DriverTrip] Failed to load fork booking details:", err);
    }
    return found;
  };

  const getVehicleById = async (id) => {
    const vehicles = await getVehicles();
    return vehicles.find((v) => v.vehicleId?.toString() === id?.toString()) || null;
  };

  const getEmployeeByServiceNo = async (serviceNo) => {
    const users = await getUsers();
    return users.find((u) => u.ServiceNo?.toString() === serviceNo?.toString()) || null;
  };

  const resolveSubBookingForVehicle = (bookingData) => {
    if (!bookingData) return null;
    const subBookings = bookingData.SubBookings || bookingData.subBookings || bookingData.subBooking || bookingData.subBookingDetails || [];
    if (!Array.isArray(subBookings) || subBookings.length === 0) return null;
    const vId = String(bookingData?.vId || "");
    return subBookings.find((sub) => String(sub?.subvid || sub?.subvId || sub?.vId || sub?.vid || "") === vId) || null;
  };

  const getDisplayLocations = (bookingData) => {
    if (!bookingData) return { from: "", to: "" };
    const baseFrom = bookingData.From || bookingData.fromLocation || bookingData.WorkArea || "";
    const baseTo = bookingData.TO || bookingData.toLocation || "";
    if (baseFrom || baseTo) return { from: baseFrom, to: baseTo };
    const sub = resolveSubBookingForVehicle(bookingData);
    if (!sub) return { from: "", to: "" };
    return { from: sub.subFrom || sub.From || sub.from || "", to: sub.subTO || sub.TO || sub.to || "" };
  };

  const determineInitialTripStage = (bookingData) => {
    if (!bookingData) return "accepted";
    const status = bookingData.status?.toUpperCase()?.trim() || "";
    if (status === "A" || status === "ACTIVE") return "accepted";
    else if (status === "EN" || status === "ENROUTE") return "heading_to_pickup";
    else if (status === "IP" || status === "INPROGRESS" || status === "IN PROGRESS") return "trip_started";
    return "accepted";
  };

  const handleOpenEmergency = () => setIsEmergencyModalOpen(true);

  const handleSendEmergency = async ({ ecode, ediscription }) => {
    if (!booking?.vId) {
      toast.error("No vehicle assigned. Cannot send alert.");
      return;
    }
    setIsSendingEmergency(true);
    try {
      await updateEmergency(booking.vId, ecode, ediscription);
      const success = chatWS.sendEmergency({
        vehicleId: booking.vId,
        ecode: ecode,
        ediscription: ediscription,
        driverName: userDetails?.Name || "Driver",
        driverSno: userDetails?.ServiceNo || "",
        mobileNo: "N/A"
      });
      if (!success) console.warn("WebSocket send failed for emergency, but DB updated.");
      toast.success("EMERGENCY ALERT SENT! Help is on the way.");
      setIsEmergencyModalOpen(false);
    } catch (error) {
      console.error("Emergency Alert Failed:", error);
      toast.error("Failed to send alert! Please call emergency services.");
    } finally {
      setIsSendingEmergency(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            resolve({
              latitude: parseFloat(pos.coords.latitude.toFixed(6)),
              longitude: parseFloat(pos.coords.longitude.toFixed(6)),
            });
          },
          () => resolve(driverLocation),
          { enableHighAccuracy: true, timeout: 10000 }
        );
      } else {
        resolve(driverLocation);
      }
    });
  };

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 + Math.cos(lat1 * Math.PI/180) * Math.cos(lat2 * Math.PI/180) * Math.sin(dLon/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const initializeHireBubble = (bookingData) => {
    if (!bookingData) return;
    const pickup = ensureValidLatLng(bookingData.slatitude, bookingData.slongitude);
    const destination = ensureValidLatLng(bookingData.elatitude, bookingData.elongitude);
    if (!pickup || !destination) return;
    const distance = calculateDistance(
      { lat: pickup[0], lng: pickup[1] },
      { lat: destination[0], lng: destination[1] }
    );
    const eta = estimateETA(distance);
    const fare = calculateFare(distance);
    setActiveHire({
      hireId: bookingData.bId,
      bookingId: bookingData.bId,
      status: tripStage,
      pickupLocation: { lat: pickup[0], lng: pickup[1] },
      destinationLocation: { lat: destination[0], lng: destination[1] },
      distance: distance,
      eta: eta,
      fare: fare,
    });
    setHireStage(tripStage);
  };

  const updateDriverLocation = async () => {
    const loc = await getCurrentLocation();
    if (!loc) return;
    setDriverLocation(loc);
    if (booking) {
      const pickup = ensureValidLatLng(booking.slatitude, booking.slongitude);
      const dest = ensureValidLatLng(booking.elatitude, booking.elongitude);
      if (pickup) setDistanceToPickup(haversineDistance(loc.latitude, loc.longitude, pickup[0], pickup[1]).toFixed(1));
      if (dest) setDistanceToDestination(haversineDistance(loc.latitude, loc.longitude, dest[0], dest[1]).toFixed(2));
    }
    if (booking?.vId) {
      try {
        const payload = [{ vehicleId: booking.vId, status: "A", latitude: loc.latitude, longitude: loc.longitude }];
        await api.post("/VehicleRegister/UpdateVehicleLocation", payload);
        if (locationWS.isReady()) {
          locationWS.updateDriverData({ latitude: loc.latitude, longitude: loc.longitude, bStatus: "B" });
        }
      } catch (err) {
        console.error("Location update failed", err);
      }
    }
  };

  const resetVehicleBStatus = async () => {
    if (!booking?.vId) return;
    const loc = await getCurrentLocation();
    let vehicle = null;
    try {
      vehicle = await getVehicleById(booking.vId);
    } catch (err) {
      console.error("Failed to fetch vehicle before reset bStatus:", err.response?.data || err.message);
    }
    const typeCodeMap = {
      Car: "C", Van: "V", Lorry: "L", SUV: "S", Cab: "CA",
      Bus: "B", "Cherry Picker": "CH", Forklift: "F", Crane: "CR", Trailer: "T",
    };
    const vehicleTypeCode = typeCodeMap[vehicle?.vehicleType] || vehicle?.vehicleType || "C";
    const statusCode = vehicle?.status === "Inactive" ? "I" : vehicle?.status === "Active" ? "A" : "A";
    const payload = [{
      vehicleId: vehicle?.vehicleId || booking.vId,
      vehicleType: vehicleTypeCode,
      weightCat: vehicle?.weightCat || "",
      driverSno: vehicle?.driverSno || userDetails?.ServiceNo || "",
      status: statusCode,
      bStatus: "",
      oStatus: "O",
      latitude: loc?.latitude ?? vehicle?.latitude ?? "",
      longitude: loc?.longitude ?? vehicle?.longitude ?? "",
    }];
    try {
      await api.post("/VehicleRegister/PostVehicleDetails", payload);
      locationWS.updateDriverData({
        vehicleId: vehicle?.vehicleId || booking.vId,
        vehicle_type: vehicle?.vehicleType || "Car",
        vehicleType: vehicle?.vehicleType || "Car",
        latitude: payload[0].latitude,
        longitude: payload[0].longitude,
        bStatus: "",
        oStatus: "O",
        status: statusCode,
      });
      if (locationWS.isReady()) locationWS.sendLocationUpdate();
    } catch (err) {
      console.error("Failed to reset vehicle bStatus:", err.response?.data || err.message);
    }
  };

  const loadRoutes = async () => {
    if (!booking || !driverLocation) return;
    const pickup = ensureValidLatLng(booking.slatitude, booking.slongitude);
    const dest = ensureValidLatLng(booking.elatitude, booking.elongitude);
    if (!pickup || !dest) return;
    const driver = [driverLocation.latitude, driverLocation.longitude];
    const toPickup = await fetchRoute(driver, pickup);
    if (toPickup) setRouteToPickup(toPickup);
    const toDest = await fetchRoute(pickup, dest);
    if (toDest) setRouteToDestination(toDest);
  };

  const fetchEmployeePhoneNumber = async (employeeServiceNo) => {
    if (!employeeServiceNo) {
      setEmployeePhoneNumber(null);
      return;
    }
    try {
      const employee = await getEmployeeByServiceNo(employeeServiceNo);
      if (employee && employee.MobileNo) setEmployeePhoneNumber(employee.MobileNo);
      else setEmployeePhoneNumber(null);
    } catch (err) {
      console.error("Failed to fetch employee phone number:", err);
      setEmployeePhoneNumber(null);
    }
  };

  const handleStartToPickup = async () => {
    setIsActionLoading(true);
    try {
      if (!booking?.bId) {
        toast.error("No booking loaded");
        return;
      }
      const payload = [{ bId: String(booking.bId), status: "EN" }];
      await postForkBookingForDriver(payload);
      bookingWS.sendStatusUpdate({
        bId: booking.bId,
        status: "EN",
        dlatitude: driverLocation?.latitude,
        dlongitude: driverLocation?.longitude,
        vId: booking.vId,
        dSNo: userDetails?.ServiceNo,
      });
      setBooking({ ...booking, status: "EN" });
      setTripStage("heading_to_pickup");
      initializeHireBubble({ ...booking, status: "EN" });
      toast.success("✅ Heading to pickup location");
    } catch (err) {
      console.error("[DriverTrip] Failed to start trip to pickup:", err);
      toast.error("Failed to update status. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleArrivedAtPickup = async () => {
    setIsActionLoading(true);
    try {
      if (!booking?.bId) {
        toast.error("No booking loaded");
        return;
      }
      const payload = [{ bId: String(booking.bId), status: "IP" }];
      await postForkBookingForDriver(payload);
      bookingWS.sendStatusUpdate({
        bId: booking.bId,
        status: "IP",
        dlatitude: driverLocation?.latitude,
        dlongitude: driverLocation?.longitude,
        vId: booking.vId,
        dSNo: userDetails?.ServiceNo,
      });
      setBooking({ ...booking, status: "IP" });
      setTripStage("trip_started");
      initializeHireBubble({ ...booking, status: "IP" });
      toast.success("✅ Trip started! Heading to destination");
    } catch (err) {
      console.error("[DriverTrip] Failed to start trip:", err);
      toast.error("Failed to update status. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCompleteTrip = async () => {
    setIsActionLoading(true);
    try {
      if (!booking?.bId) {
        toast.error("No booking loaded");
        return;
      }
      const now = new Date();
      const isoDate = now.toISOString().slice(0, 10);
      const timeStr = now.toTimeString().slice(0, 8);
      const completionTime = `${isoDate} ${timeStr}`;
      const payload = [{ bId: String(booking.bId), status: "CO", beTime: completionTime }];
      await postForkBookingForDriver(payload);
      bookingWS.sendStatusUpdate({
        bId: booking.bId,
        status: "CO",
        dlatitude: driverLocation?.latitude,
        dlongitude: driverLocation?.longitude,
        vId: booking.vId,
        dSNo: userDetails?.ServiceNo,
        beTime: completionTime,
      });
      setBooking({ ...booking, status: "CO", beTime: completionTime });
      setTripStage("completed");
      initializeHireBubble({ ...booking, status: "CO" });
      await resetVehicleBStatus();
      toast.success("✅ Trip completed! Thank you.");
      setTimeout(() => navigate("/driver/dashboard"), 2000);
    } catch (err) {
      console.error("[DriverTrip] Failed to complete trip:", err);
      toast.error("Failed to complete trip. Please try again.");
    } finally {
      setIsActionLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (!bookingId) {
      setLoadError("No booking ID provided");
      setIsLoading(false);
      return;
    }
    const loadBooking = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const b = await getLatestBookingById(bookingId);
        if (b) {
          setBooking(b);
          try {
            const vehicle = await getVehicleById(b.vId);
            if (vehicle?.vehicleType) setVehicleType(vehicle.vehicleType);
          } catch (vErr) {
            console.error("[DriverTrip] Failed to fetch vehicle type:", vErr);
          }
          const stage = determineInitialTripStage(b);
          setTripStage(stage);
          initializeHireBubble(b);
          fetchEmployeePhoneNumber(b.bookingSno || b.BookingSno);
          setIsLoading(false);
        } else {
          setLoadError(`Booking #${bookingId} not found`);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("[DriverTrip] Failed to fetch booking:", err);
        setLoadError("Failed to load trip details");
        setIsLoading(false);
      }
      if (authKey && userDetails?.ServiceNo) {
        chatWS.connect(authKey, userDetails.ServiceNo, "Driver");
      }
    };
    loadBooking();
  }, [bookingId, authKey, userDetails?.ServiceNo]);

  // Get initial location
  useEffect(() => {
    const getInitialLocation = async () => {
      const loc = await getCurrentLocation();
      if (loc) setDriverLocation(loc);
    };
    getInitialLocation();
  }, []);

  // Start location updates and load routes
  useEffect(() => {
    if (booking) initializeHireBubble(booking);
  }, [booking, tripStage]);

  useEffect(() => {
    if (!booking || !driverLocation) return;
    loadRoutes();
    locationInterval.current = setInterval(updateDriverLocation, 15000);
    if (authKey) {
      locationWS.connect(authKey, {
        vehicleId: booking.vId,
        driverSno: userDetails?.ServiceNo,
        driverName: userDetails?.Name,
        latitude: driverLocation.latitude,
        longitude: driverLocation.longitude,
        status: "Active",
        bStatus: "Booking",
        oStatus: "O",
      });
    }
    return () => {
      if (locationInterval.current) clearInterval(locationInterval.current);
    };
  }, [booking, driverLocation?.latitude]);

  useEffect(() => {
    if (!booking?.vId || !authKey) return;
    trackingWS.connect(authKey);
    const unsubscribe = trackingWS.subscribe(booking.vId, (msg) => {
      if (msg && msg.latitude != null && msg.longitude != null) {
        setDriverLocation({ latitude: msg.latitude, longitude: msg.longitude });
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [booking?.vId, authKey]);

  useEffect(() => {
    if (!booking?.bId || !authKey || !userDetails?.ServiceNo) return;
    bookingWS.connect(authKey, userDetails.ServiceNo, booking.vId);
    bookingWS.onNewBooking((msg) => {
      if (msg.dSNo && userDetails?.ServiceNo && msg.dSNo.toString() !== userDetails.ServiceNo.toString()) return;
      if (msg.type === "booking_cancelled" || msg.action === "booking_cancelled") {
        if (msg.bId?.toString() === booking.bId?.toString()) {
          toast.error("🚫 Hire cancelled by employee!", { position: "top-center", autoClose: 4000 });
          resetVehicleBStatus();
          setTimeout(() => navigate("/driver/dashboard"), 1500);
        }
      }
    });
    return () => {
      bookingWS.onNewBooking(null);
    };
  }, [booking?.bId, booking?.vId, authKey, userDetails?.ServiceNo, navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const isActiveTrip = booking && tripStage !== "completed" && booking.status !== "C" && booking.status !== "CANCELLED";
      if (isActiveTrip) {
        e.preventDefault();
        e.returnValue = "You have an active trip in progress. Are you sure you want to leave?";
        return e.returnValue;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [booking, tripStage]);

  useEffect(() => {
    const now = Date.now();
    if (!booking || !driverLocation) return;
    if (now - lastRouteRefresh.current < 30000) return;
    lastRouteRefresh.current = now;
    loadRoutes();
  }, [driverLocation?.latitude, driverLocation?.longitude, booking, tripStage]);

  // Loading state
  if (isLoading) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Loading trip details...</p>
          </div>
        </div>
      </DriverLayout>
    );
  }

  // Error state
  if (loadError) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center min-h-[60vh] p-4">
          <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Trip</h2>
            <p className="text-sm text-gray-500 mb-6">{loadError}</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => window.location.reload()} className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg hover:bg-gray-800 transition flex items-center gap-2">
                Retry
              </button>
              <button onClick={() => navigate("/driver/dashboard")} className="px-4 py-2 border border-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition">
                Dashboard
              </button>
            </div>
          </div>
        </div>
      </DriverLayout>
    );
  }

  // Waiting for location
  if (!booking || !driverLocation) {
    return (
      <DriverLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Getting your location...</p>
            <p className="text-gray-400 text-xs mt-1">Please enable location access</p>
          </div>
        </div>
      </DriverLayout>
    );
  }

  const pickup = ensureValidLatLng(booking.slatitude, booking.slongitude);
  const destination = ensureValidLatLng(booking.elatitude, booking.elongitude);
  const driverPos = [driverLocation.latitude, driverLocation.longitude];
  const hasValidCoords = !!pickup && !!destination;
  const showPickupRoute = tripStage === "accepted" || tripStage === "heading_to_pickup";
  const showDestRoute = tripStage === "trip_started" || tripStage === "completed";
  const mapBounds = hasValidCoords ? (showPickupRoute ? [driverPos, pickup] : [pickup, destination]) : [driverPos];
  const stageConfig = getStageConfig(tripStage);

  return (
    <DriverLayout>
      {/* Main Content */}
      <div className="max-w-4xl mx-auto">
        {/* Page Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
            Active Trip
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Manage your current booking and navigation
          </p>
        </div>

        {/* Booking Header Card */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden mb-4">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stageConfig.bgLight}`}>
                <stageConfig.icon size={20} className={stageConfig.color.split(' ')[1]} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Booking ID</p>
                <p className="text-sm font-mono font-semibold text-gray-900">#{booking.bId}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${stageConfig.color}`}>
                {stageConfig.label}
              </span>
            </div>
          </div>

          {/* Employee Info */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <Users size={18} className="text-gray-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Requested by</p>
                <p className="text-sm font-medium text-gray-900">{booking.employeeName || "Employee"}</p>
                {booking.bookingSno && <p className="text-xs text-gray-400">ID: {booking.bookingSno}</p>}
              </div>
            </div>
          </div>

          {/* Location Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
            {/* Pickup */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-emerald-50 rounded-full flex items-center justify-center">
                  <Circle size={12} className="text-emerald-500 fill-emerald-500" />
                </div>
                <p className="text-xs font-medium text-gray-700">Pickup Location</p>
              </div>
              <p className="text-sm text-gray-900 font-mono break-all">
                {pickup ? `${pickup[0].toFixed(6)}, ${pickup[1].toFixed(6)}` : (getDisplayLocations(booking).from || "N/A")}
              </p>
              {distanceToPickup && (
                <p className="text-xs text-gray-500 mt-2">{distanceToPickup} km away</p>
              )}
            </div>

            {/* Destination */}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-blue-50 rounded-full flex items-center justify-center">
                  <Flag size={12} className="text-blue-500" />
                </div>
                <p className="text-xs font-medium text-gray-700">Destination</p>
              </div>
              <p className="text-sm text-gray-900 font-mono break-all">
                {destination ? `${destination[0].toFixed(6)}, ${destination[1].toFixed(6)}` : (getDisplayLocations(booking).to || "N/A")}
              </p>
              {booking.totalDestence && (
                <p className="text-xs text-gray-500 mt-2">Trip: {Number(booking.totalDestence).toFixed(2)} km</p>
              )}
            </div>
          </div>
        </div>

        {/* Map Card */}
        <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden mb-4">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapIcon size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-700">Live Route</span>
            </div>
            {tripStage === "heading_to_pickup" && routeToPickup.length > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-xs text-gray-400">Route to pickup</span>
              </div>
            )}
            {tripStage === "trip_started" && routeToDestination.length > 0 && (
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span className="text-xs text-gray-400">Route to destination</span>
              </div>
            )}
          </div>
          <div className="h-80 w-full">
            <style>{`.leaflet-container { z-index: 1 !important; } .leaflet-popup { z-index: 2 !important; } .leaflet-control { z-index: 2 !important; }`}</style>
            <MapContainer center={driverPos} zoom={14} style={{ height: "100%", width: "100%", zIndex: 1 }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {hasValidCoords && mapBounds.length >= 2 && <FitBounds bounds={mapBounds} />}
              
              {/* Driver Marker */}
              <Marker position={driverPos} icon={getVehicleIcon(vehicleType)}>
                <Popup><strong>🚗 Your Location</strong><br />{booking.vId}</Popup>
              </Marker>
              
              {/* Pickup Marker */}
              {hasValidCoords && pickup && (
                <Marker position={pickup} icon={pickupIcon}>
                  <Popup><strong>📍 Pickup</strong><br />{distanceToPickup} km away</Popup>
                </Marker>
              )}
              
              {/* Destination Marker */}
              {hasValidCoords && (tripStage === "trip_started" || tripStage === "completed") && destination && (
                <Marker position={destination} icon={destinationIcon}>
                  <Popup><strong>🎯 Destination</strong><br />{Number(booking.totalDestence || 0).toFixed(2)} km trip</Popup>
                </Marker>
              )}
              
              {/* Route Lines */}
              {hasValidCoords && showPickupRoute && routeToPickup.length > 0 && (
                <Polyline positions={routeToPickup} color="#3b82f6" weight={5} opacity={0.8} dashArray="8, 8" />
              )}
              {hasValidCoords && showDestRoute && routeToDestination.length > 0 && (
                <Polyline positions={routeToDestination} color="#8b5cf6" weight={5} opacity={0.8} />
              )}
            </MapContainer>
          </div>
        </div>

        {/* Distance Info Card */}
        {tripStage !== "completed" && (
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-4 mb-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-1">Distance to Pickup</p>
                <p className="text-xl font-semibold text-gray-900">{distanceToPickup ? Number(distanceToPickup).toFixed(1) : "---"} <span className="text-xs font-normal">km</span></p>
              </div>
              <div className="text-center border-l border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Total Trip Distance</p>
                <p className="text-xl font-semibold text-gray-900">{booking.totalDestence ? Number(booking.totalDestence).toFixed(1) : "---"} <span className="text-xs font-normal">km</span></p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons with Slider Pattern */}
        <div className="space-y-4">
          {tripStage === "accepted" && (
            <SliderButton
              onConfirm={handleStartToPickup}
              icon={Navigation2}
              label="Start Trip"
              confirmLabel="Starting..."
              color="emerald"
              isLoading={isActionLoading}
            />
          )}

          {tripStage === "heading_to_pickup" && (
            <>
              <CallEmployeeButton
                employeeName={booking.employeeName || "Employee"}
                employeePhone={employeePhoneNumber}
                showSMS={true}
                className="w-full"
                buttonSize="md"
              />
              <SliderButton
                onConfirm={handleArrivedAtPickup}
                icon={MapPin}
                label="Confirm Arrival"
                confirmLabel="Arriving..."
                color="blue"
                isLoading={isActionLoading}
              />
            </>
          )}

          {tripStage === "trip_started" && (
            <>
              <CallEmployeeButton
                employeeName={booking.employeeName || "Employee"}
                employeePhone={employeePhoneNumber}
                showSMS={true}
                className="w-full"
                buttonSize="md"
              />
              <SliderButton
                onConfirm={handleCompleteTrip}
                icon={CheckCircle}
                label="Complete Trip"
                confirmLabel="Completing..."
                color="purple"
                isLoading={isActionLoading}
              />
            </>
          )}

          {tripStage === "completed" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle size={20} className="text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700">Trip Completed!</span>
              </div>
              <p className="text-xs text-emerald-600">Thank you for completing this trip</p>
            </div>
          )}
        </div>

        {/* Coordinates Warning */}
        {!hasValidCoords && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle size={14} />
              Coordinates are missing for this booking. Route and map pins will appear once coordinates are available.
            </p>
          </div>
        )}
      </div>

      {/* Hire Assistance Bubble */}
      {pickup && destination && (
        <HireAssistanceBubble
          tripStage={tripStage}
          pickupLocation={pickup ? { lat: pickup[0], lng: pickup[1] } : null}
          destinationLocation={destination ? { lat: destination[0], lng: destination[1] } : null}
          driverLocation={driverLocation ? { lat: driverLocation.latitude, lng: driverLocation.longitude } : null}
          onEmergency={handleOpenEmergency}
        />
      )}

      <EmergencyAlertModal
        isOpen={isEmergencyModalOpen}
        onClose={() => setIsEmergencyModalOpen(false)}
        onSend={handleSendEmergency}
        isSending={isSendingEmergency}
      />
    </DriverLayout>
  );
};

export default DriverTrip;